/**
 * @classdesc Classe utilitária para agrupar métodos de manipulação de cadeia de caracteres.
 */
export default class StringUtil {
    /**
     * Substitui caracteres UTF-8 por caracteres equivalentes Latin1 (ISO-8859-1).
     *
     * Substitui por acento grave:
     * 96	U+0060	60	`	Grave Accent
     * 768	U+0300	CC 80	̀	Combining Grave Accent
     * 715	U+02CB	CB 8B	ˋ	Modifier Letter Grave Accent
     *
     * Substitui por apóstrofo (aspas simples):
     * 39	U+0027	27	'	Apostrophe
     * 145	U+0091	C2 91	‘	Control character: Private Use One
     * 146	U+0092	C2 92	’	Control character: Private Use Two
     * 697	U+02B9	CA B9	ʹ	Modifier Letter Prime
     * 699	U+02BB	CA BB	ʻ	Modifier Letter Turned Comma
     * 700	U+02BC	CA BC	ʼ	Modifier Letter Apostrophe
     * 701	U+02BD	CA BD	ʽ	Modifier Letter Reversed Comma
     * 702	U+02BE	CA BE	ʾ	Modifier Letter Right Half Ring
     * 703	U+02BF	CA BF	ʿ	Modifier Letter Left Half Ring
     * 712	U+02C8	CB 88	ˈ	Modifier Letter Vertical Line
     * 714	U+02CA	CB 8A	ˊ	Modifier Letter Acute Accent
     * 722	U+02D2	CB 92	˒	Modifier Letter Centred Right Half Ring
     * 723	U+02D3	CB 93	˓	Modifier Letter Centred Left Half Ring
     * 756	U+02F4	CB B4	˴	Modifier Letter Middle Grave Accent
     * 769	U+0301	CC 81	́	Combining Acute Accent
     * 786	U+0312	CC 92	̒	Combining Turned Comma Above
     * 787	U+0313	CC 93	̓	Combining Comma Above
     * 788	U+0314	CC 94	̔	Combining Reversed Comma Above
     * 789	U+0315	CC 95	̕	Combining Comma Above Right
     * 835	U+0343	CD 83	̓	Combining Greek Koronis
     * 1369	U+0559	D5 99	ՙ	Armenian Modifier Letter Left Half Ring
     * 1370	U+055A	D5 9A	՚	Armenian Apostrophe
     *
     * Substitui por aspas duplas:
     * 34	U+0022	22	"	Quotation Mark
     * 147	U+0093	C2 93	“	Control character: Set Transmit State
     * 148	U+0094	C2 94	”	Control character: Cancel Character
     * 698	U+02BA	CA BA	ʺ	Modifier Letter Double Prime
     * 733	U+02DD	CB 9D	˝	Double Acute Accent
     * 750	U+02EE	CB AE	ˮ	Modifier Letter Double Apostrophe
     * 757	U+02F5	CB B5	˵	Modifier Letter Middle Double Grave Accent
     * 758	U+02F6	CB B6	˶	Modifier Letter Middle Double Acute Accent
     * 834	U+0342	CD 82	͂	Combining Greek Perispomeni
     *
     * Substitui por hífen:
     * 45	U+002D	2D	-	Hyphen-minus
     * 150	U+0096	C2 96	–	Control character: Start Of Guarded Area
     * 151	U+0097	C2 97	—	Control character: End Of Guarded Area
     * 727	U+02D7	CB 97	˗	Modifier Letter Minus Sign
     * 821	U+0335	CC B5	̵	Combining Short Stroke Overlay
     * 822	U+0336	CC B6	̶	Combining Long Stroke Overlay
     *
     * @public
     * @static
     * @todo Implementar tratamento para os caracteres "Combining".
     * @see {@link https://www.charset.org/utf-8}
     * @param {string} texto String com caracteres UTF-8.
     * @returns {string} String com caracteres equivalentes no alfabeto ISO-8859-1.
     */
    static tratarCaracteresNaoLatin1(texto) {
        if (typeof texto === 'undefined' || (typeof texto === 'string' && texto.length === 0)) {
            return '';
        }

        if (typeof texto === 'number' || typeof texto === 'bigint') {
            return '' + texto;
        }

        // @ts-ignore
        if (!(typeof texto === 'string' || texto instanceof String)) {
            throw new TypeError(
                `Parâmetro inválido para \`tratarCaracteresNaoLatin1\`: ${typeof texto}, esperado \`string\`.`
            );
        }

        const correspondenciasCaracteres = {
            '`': ['ˋ'],
            "'": ['‘', '’', 'ʹ', 'ʻ', 'ʼ', 'ʽ', 'ʾ', 'ʿ', 'ˈ', 'ˊ', '˒', '˓', '˴', 'ՙ', '՚'],
            '"': ['“', '”', 'ʺ', '˝', 'ˮ', '˵', '˶'],
            '-': ['–', '—', '˗', '̵', '̶']
        };

        for (let caractereLatin1 in correspondenciasCaracteres) {
            let correspondenciasDeUmCaractereLatin1 = correspondenciasCaracteres[caractereLatin1];

            for (let caracteresUtf8 of correspondenciasDeUmCaractereLatin1) {
                for (let caractereUtf8 of caracteresUtf8) {
                    const regex = new RegExp(caractereUtf8, 'g');
                    texto = texto.replace(regex, caractereLatin1);
                }
            }
        }

        return texto;
    }

    /**
     * Substitui a primeira letra da string pela letra correspondente minúscula.
     *
     * @public
     * @static
     * @param {string} str String original.
     * @returns {string} String com a primeira letra em minúscula.
     */
    static substituirPrimeiraLetraPorMinuscula(str) {
        if (typeof str === 'undefined' || (typeof str === 'string' && str.length === 0)) {
            return '';
        }

        if (typeof str === 'number' || typeof str === 'bigint') {
            return '' + str;
        }

        // @ts-ignore
        if (!(typeof str === 'string' || str instanceof String)) {
            throw new TypeError(
                `Parâmetro inválido para \`substituirPrimeiraLetraPorMinuscula\`: ${typeof str}, esperado \`string\`.`
            );
        }

        let novaStr = str.charAt(0).toLowerCase();

        if (str.length > 1) {
            novaStr += str.slice(1);
        }

        return novaStr;
    }

    /**
     * Garante que apenas a primeira letra da string terá a primeira letra maiúscula.
     *
     * @public
     * @static
     * @param {string} str String original.
     * @returns {string} String com a primeira letra em maiúscula e todas as demais em minúsculas.
     */
    static apenasPrimeiraLetraMaiuscula(str) {
        if (typeof str === 'undefined' || (typeof str === 'string' && str.length === 0)) {
            return '';
        }

        if (typeof str === 'number' || typeof str === 'bigint') {
            return '' + str;
        }

        // @ts-ignore
        if (!(typeof str === 'string' || str instanceof String)) {
            throw new TypeError(
                `Parâmetro inválido para \`apenasPrimeiraLetraMaiuscula\`: ${typeof str}, esperado \`string\`.`
            );
        }

        let novaStr = str.charAt(0).toUpperCase();

        if (str.length > 1) {
            novaStr += str.substring(1).toLocaleLowerCase();
        }

        return novaStr;
    }

    /**
     * Converte um número, mesmo que seja maior que Number.MAX_SAFE_INTEGER (9007199254740991), em uma string numérica.
     *
     * @public
     * @static
     * @param {string|number|bigint|Array} numero Número em qualquer formato.
     * @returns {string} String numérica.
     */
    static converterNumeroEmStringNumerica(numero) {
        if (typeof numero === 'string') {
            return numero;
        }

        if (numero instanceof String) {
            // @ts-ignore
            return numero;
        }

        if (typeof numero === 'number' || typeof numero === 'bigint') {
            return '' + numero;
        }

        if (Array.isArray(numero)) {
            if (numero.length === 0) {
                return '';
            }

            let result = numero.every((n) => typeof n === 'number' || typeof n === 'bigint');

            if (!result) {
                throw new TypeError(
                    'Parâmetro inválido para `converterNumeroEmStringNumerica`. Esperado um array contendo apenas números.'
                );
            }

            let numberArr = [...numero].reduce((acc, n) => acc.concat(+n), []);
            return numberArr.join('');
        }

        throw new TypeError('Parâmetro inválido para `converterNumeroEmStringNumerica`.');
    }

    /**
     * Converte uma string booleana para um valor booleano.
     *
     * @public
     * @static
     * @param {string | number | bigint | boolean | undefined} strBol Booleano em qualquer formato.
     * @returns {boolean | undefined} Valor booleano.
     */
    static stringToBoolean(strBol) {
        if (typeof strBol === 'boolean') {
            return strBol;
        }

        if (typeof strBol === 'boolean' || typeof strBol === 'number' || typeof strBol === 'bigint') {
            if (strBol !== 0 && strBol !== 1) {
                throw new TypeError(`Valor de parâmetro inválido para \`stringToBoolean\` [${strBol}].`);
            }

            return !!strBol;
        }

        if (typeof strBol === 'string' || !strBol) {
            const str = strBol?.toLowerCase()?.trim();

            switch (str) {
                case 'true':
                case 'yes':
                case 'sim':
                case '1':
                    return true;
                case 'false':
                case 'no':
                case 'nao':
                case 'não':
                case '0':
                case null:
                case undefined:
                    return false;
                default:
                    throw new TypeError(`Valor de parâmetro inválido para \`stringToBoolean\` [${strBol}].`);
            }
        }

        throw new TypeError('Tipo de parâmetro inválido para `stringToBoolean`. Esperado string, number ou bigint.');
    }

    /**
     * Gera um resumo (digest) com FNV-1a (Fowler–Noll–Vo hash) 32-bit.
     *
     * @public
     * @static
     * @param {string} str Texto em claro.
     * @returns {string} Valor hexadecimal do hash calculado.
     */
    static fnv1a32(str) {
        if (typeof str !== 'string') {
            throw new TypeError(`Parâmetro inválido para \`fnv1a32\`: ${typeof str}, esperado \`string\`.`);
        }

        let hash = 0x811c9dc5; // offset basis (32-bit)

        for (let i = 0; i < str.length; i++) {
            hash ^= str.charCodeAt(i);
            hash = (hash * 0x01000193) >>> 0; // FNV prime, 32-bit, >>> 0 força unsigned
        }

        return hash.toString(16);
    }

    /**
     * Conversão de string para formato padronizado de caracteres.
     *
     * @public
     * @static
     * @param {string} str Texto que será convertido.
     * @returns {string} Texto normalizado.
     */
    static normalizarString(str) {
        return str
            ? (str + '')
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')
                  .toLowerCase()
            : '';
    }
}
